import { clamp01, evaluateEasing } from "./easing";
import {
  evaluateMotionKeyframes,
  getMotionProperty,
  seededMotionNoise,
  setMotionProperty,
} from "./interpolation";
import { sampleMotionPath } from "./path";
import type { MotionElement, MotionPreset } from "./types";
export function motionElementLocalTime(
  element: MotionElement,
  time: number,
): number {
  const duration = element.end - element.start;
  const elapsed =
    (time - element.start) *
    (element.type === "particles" ? 1 : (element.speed ?? 1));
  return element.loop &&
    element.type !== "video" &&
    element.type !== "particles" &&
    duration > 0
    ? ((elapsed % duration) + duration) % duration
    : elapsed;
}
function applyPreset(
  result: MotionElement,
  preset: MotionPreset,
  progress: number,
  exiting: boolean,
): void {
  const p = clamp01(progress);
  const hidden = 1 - p;
  const opacity = result.opacity ?? 1;
  const distance = preset.type === "drop" ? 400 : 120;
  if (
    [
      "fade",
      "slide",
      "pop",
      "zoom",
      "blur",
      "flip",
      "drop",
      "bounce",
      "glitch",
    ].includes(preset.type)
  )
    result.opacity = opacity * p;
  if (
    preset.type === "slide" ||
    preset.type === "drop" ||
    preset.type === "bounce"
  ) {
    const direction =
      preset.direction ?? (preset.type === "drop" ? "up" : "down");
    const shift =
      distance *
      (preset.type === "bounce" ? 1 - evaluateEasing("bounce", p) : hidden);
    if (direction === "left" || direction === "right")
      result.x = (result.x ?? 0) + shift * (direction === "left" ? -1 : 1);
    else result.y = (result.y ?? 0) + shift * (direction === "up" ? -1 : 1);
  }
  if (preset.type === "pop")
    result.scale =
      (result.scale ?? 1) * (0.5 + 0.5 * evaluateEasing("back", p));
  if (preset.type === "zoom")
    result.scale = (result.scale ?? 1) * (0.5 + 0.5 * p);
  if (preset.type === "blur") result.blur = (result.blur ?? 0) + 24 * hidden;
  if (preset.type === "flip") {
    result.rotateY = (result.rotateY ?? 0) + 90 * hidden;
    result.perspective ??= 1000;
  }
  if (preset.type === "wipe") {
    const clip = typeof result.clip === "object" ? { ...result.clip } : {};
    const side =
      preset.direction === "left"
        ? "left"
        : preset.direction === "up"
          ? "top"
          : preset.direction === "down"
            ? "bottom"
            : "right";
    clip[side] =
      hidden *
      (side === "left" || side === "right"
        ? (result.width ?? result.maxWidth ?? 1080)
        : (result.height ?? 1920));
    result.clip = clip;
  }
  if (preset.type === "draw") {
    if (exiting) result.trimStart = hidden;
    else result.trimEnd = p;
  }
  if (preset.type === "typewriter" && result.type === "text")
    result.content = Array.from(result.content ?? "")
      .slice(0, Math.floor(Array.from(result.content ?? "").length * p))
      .join("");
  if (preset.type === "glitch") {
    result.x = (result.x ?? 0) + seededMotionNoise(13, p * 30) * hidden * 30;
    result.skewX =
      (result.skewX ?? 0) + seededMotionNoise(17, p * 20) * hidden * 8;
  }
}
/** Pure frame evaluator. The supplied time is relative to the enclosing motion layer. */
export function evaluateMotionElement(
  element: MotionElement,
  time: number,
): MotionElement {
  const result = JSON.parse(JSON.stringify(element)) as MotionElement;
  const local = motionElementLocalTime(element, time);
  const duration = element.end - element.start;
  for (const [property, frames] of Object.entries(element.keyframes ?? {}))
    setMotionProperty(result, property, evaluateMotionKeyframes(frames, local));
  if (element.followPath) {
    const progress = Number(
      evaluateMotionKeyframes(element.followPath.progress, local),
    );
    const position = sampleMotionPath(element.followPath.d, progress);
    result.x = (result.x ?? 0) + position.x;
    result.y = (result.y ?? 0) + position.y;
    if (element.followPath.autoRotate)
      result.rotation = (result.rotation ?? 0) + position.rotation;
  }
  if (element.wiggle) {
    const { prop, amount, frequency, seed } = element.wiggle;
    setMotionProperty(
      result,
      prop,
      Number(getMotionProperty(result, prop) ?? 0) +
        amount * seededMotionNoise(seed, local * frequency),
    );
  }
  for (const loop of element.loops ?? []) {
    const phase = local / (loop.duration ?? 2);
    const sine = Math.sin(phase * Math.PI * 2);
    const amount = loop.amount ?? (loop.type === "pulse" ? 0.1 : 20);
    if (loop.type === "float") result.y = (result.y ?? 0) + sine * amount;
    if (loop.type === "pulse")
      result.scale = (result.scale ?? 1) * (1 + sine * amount);
    if (loop.type === "spin")
      result.rotation = (result.rotation ?? 0) + phase * 360;
    if (loop.type === "wiggle")
      result.rotation =
        (result.rotation ?? 0) +
        seededMotionNoise(loop.seed ?? 1, phase * 5) * amount;
    if (loop.type === "shake")
      result.x =
        (result.x ?? 0) +
        seededMotionNoise(loop.seed ?? 1, phase * 20) * amount;
    if (loop.type === "blink")
      result.opacity = (result.opacity ?? 1) * (phase % 1 < 0.5 ? 1 : 0);
    if (loop.type === "orbit") {
      result.x = (result.x ?? 0) + Math.cos(phase * Math.PI * 2) * amount;
      result.y = (result.y ?? 0) + sine * amount;
    }
  }
  if (element.type === "text") {
    if (element.countUp) {
      const count = element.countUp;
      const value =
        count.from + (count.to - count.from) * clamp01(local / duration);
      const formatted = count.locale
        ? new Intl.NumberFormat(count.locale, {
            minimumFractionDigits: count.decimals ?? 0,
            maximumFractionDigits: count.decimals ?? 0,
          }).format(value)
        : value.toFixed(count.decimals ?? 0);
      result.content = `${count.prefix ?? ""}${formatted}${count.suffix ?? ""}`;
    }
    if (element.typewriter) {
      const revealDuration =
        typeof element.typewriter === "object"
          ? (element.typewriter.duration ?? duration)
          : duration;
      result.content = Array.from(result.content ?? "")
        .slice(
          0,
          Math.floor(
            Array.from(result.content ?? "").length *
              clamp01(local / revealDuration),
          ),
        )
        .join("");
    }
  }
  if (element.enter)
    applyPreset(
      result,
      element.enter,
      local / (element.enter.duration ?? 0.5),
      false,
    );
  if (element.exit)
    applyPreset(
      result,
      element.exit,
      (duration - local) / (element.exit.duration ?? 0.5),
      true,
    );
  if (time < element.start || time >= element.end || !Number.isFinite(time))
    result.opacity = 0;
  result.opacity = clamp01(result.opacity ?? 1);
  // Children remain authored: the renderer evaluates each once at the same layer clock.
  return result;
}
